// @givmo/app-admin — the server's price for a company gift or match payment (CAM-BE13). ★ MONEY ★
//
// D-CAM-40: a company gift and a company match payment may cover their fees, as a company deposit
// may (CAM-BE11, D-CAM-37). The Give and approve dialogs state what the card is charged (or the
// wallet debited) BEFORE any charge, so every figure they state comes from the server, priced by the
// resolution the charge itself uses — the stored card's brand row, the company's own cover-fees
// setting, its negotiated platform rate:
//
//   GET  /admin/company/donations/quote?amount=&payment_method=&card_id=     a gift, both ways
//   POST /admin/company/matches/quote  {units:[{amount}], payment_method, card_id}   match payments
//
// Both answer both requests a dialog can send (`uncovered`: `cover_fees` false or absent; `covered`:
// `cover_fees: true`). The match quote prices each unit as its own charge and totals them, so Approve
// all states the server's totals — an upper bound, since each leg is capped as it charges — and never
// sums money itself. Never price from `GET /fees/processor`: it withholds the card-brand rows by
// design (`agent_docs/apps.md`, the Add funds deposit).
//
// The routes landed after the committed OpenAPI snapshot, so they are called through the configured
// client (`agent_docs/api-client.md` § "A whole ROUTE the snapshot lacks"); graduate to the generated
// ops when a routine regen absorbs them. The decode FAILS CLOSED: every figure lands in a sentence the
// admin confirms and in the `expected_amount_charged` / `expected_fee` the charge is held to, so any
// missing, non-decimal or mistyped field makes the whole quote unusable, and the dialog holds.

import { givmoClient } from '@givmo/api-client';

/** The gift quote's route. */
export const GIFT_QUOTE_URL = '/admin/company/donations/quote';
/** The match payment quote's route. */
export const MATCH_QUOTE_URL = '/admin/company/matches/quote';

/** What a charge is paid from: a saved company card, or the company wallet. */
export type QuoteFunding =
  | { readonly kind: 'card'; readonly cardId: string }
  | { readonly kind: 'wallet' };

/** One request a dialog can send, priced — money as the exact decimal strings served. */
export interface ChargeQuoteBreakdown {
  /** The gross the card is charged, or the wallet debited. */
  readonly amountCharged: string;
  /** What the request pays in fees: out of the gift when not covered, on top of it when covered. */
  readonly fee: string;
  /** The card-processing part of the fees (0 on the wallet rail). */
  readonly processorFee: string;
  /** The Givmo platform part (a company's negotiated rate; 0 by default). */
  readonly platformFee: string;
  /**
   * What the gift or match delivers for charitable use. INFORMATIONAL: no dialog states it or sends
   * it, and it is signed — an uncovered card charge smaller than the card's fixed fee delivers less
   * than nothing (a $0.20 budget-capped match pays a $0.26 fee: "-0.06"), which the backend prices
   * and books as it always has.
   */
  readonly amountToCharity: string;
  /** Whether this request is booked as covering the fees. */
  readonly coverFees: boolean;
}

/** Both requests for one charge. */
export interface ChargeQuote {
  /** The company's own setting covers every charge; both breakdowns are then covered and equal. */
  readonly companyCoversFees: boolean;
  readonly uncovered: ChargeQuoteBreakdown;
  readonly covered: ChargeQuoteBreakdown;
}

/** A match quote: the totals across its units, then each unit on its own, in request order. */
export interface MatchQuote extends ChargeQuote {
  readonly units: readonly ChargeQuote[];
}

/** The wire `payment_method` + `card_id` for a funding source. */
function fundingParams(funding: QuoteFunding): { payment_method: string; card_id?: string } {
  return funding.kind === 'wallet'
    ? { payment_method: 'Wallet' }
    : { payment_method: 'Credit Card', card_id: funding.cardId };
}

/** A funding source's part of a query key: a different rail or card is a different price. */
function fundingKey(funding: QuoteFunding): string {
  return funding.kind === 'wallet' ? 'wallet' : `card:${funding.cardId}`;
}

/** The TanStack Query key for a gift quote. */
export function giftQuoteQueryKey(amount: string, funding: QuoteFunding): readonly unknown[] {
  return ['company-gift-quote', amount, fundingKey(funding)];
}

/** The TanStack Query key for a match payment quote. */
export function matchQuoteQueryKey(
  amounts: readonly string[],
  funding: QuoteFunding,
): readonly unknown[] {
  return ['company-match-quote', amounts.join(','), fundingKey(funding)];
}

/** Read a gift quote. `amount` is the wire decimal string ("250.00"). The body goes to the decode. */
export async function fetchGiftQuote(amount: string, funding: QuoteFunding): Promise<unknown> {
  const { data } = await givmoClient.get({
    url: GIFT_QUOTE_URL,
    query: { amount, ...fundingParams(funding) },
    throwOnError: true,
  });
  return data;
}

/** Read a match payment quote, one unit per amount (wire decimal strings), in charge order. */
export async function fetchMatchQuote(
  amounts: readonly string[],
  funding: QuoteFunding,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: MATCH_QUOTE_URL,
    body: { units: amounts.map((amount) => ({ amount })), ...fundingParams(funding) },
    throwOnError: true,
  });
  return data;
}

const PLAIN_DECIMAL = /^\d+(?:\.\d+)?$/;
const SIGNED_DECIMAL = /^-?\d+(?:\.\d+)?$/;

function decimalField(
  row: Record<string, unknown>,
  key: string,
  pattern: RegExp = PLAIN_DECIMAL,
): string | null {
  const value = row[key];
  return typeof value === 'string' && pattern.test(value) ? value : null;
}

function toBreakdown(raw: unknown): ChargeQuoteBreakdown | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const row = raw as Record<string, unknown>;
  const amountCharged = decimalField(row, 'amount_charged');
  const fee = decimalField(row, 'fee');
  const processorFee = decimalField(row, 'processor_fee');
  const platformFee = decimalField(row, 'platform_fee');
  // Every figure a dialog states or sends is an unsigned decimal. What reaches the charity is stated
  // nowhere and may be negative (see `ChargeQuoteBreakdown.amountToCharity`), so its sign never
  // turns a price the backend charges into "could not check the processing fee" (cut 13 review F4).
  const amountToCharity = decimalField(row, 'amount_to_charity', SIGNED_DECIMAL);
  if (
    amountCharged === null ||
    fee === null ||
    processorFee === null ||
    platformFee === null ||
    amountToCharity === null
  ) {
    return null;
  }
  if (typeof row.cover_fees !== 'boolean') return null;
  return {
    amountCharged,
    fee,
    processorFee,
    platformFee,
    amountToCharity,
    coverFees: row.cover_fees,
  };
}

/** Decode one quote (`CompanyChargeQuoteOut`), failing closed: any bad field → `null`. */
export function toChargeQuote(body: unknown): ChargeQuote | null {
  if (typeof body !== 'object' || body === null) return null;
  const quote = body as Record<string, unknown>;
  if (typeof quote.company_covers_fees !== 'boolean') return null;
  const uncovered = toBreakdown(quote.uncovered);
  const covered = toBreakdown(quote.covered);
  if (uncovered === null || covered === null) return null;
  return { companyCoversFees: quote.company_covers_fees, uncovered, covered };
}

/**
 * Decode a match payment quote (`CompanyMatchQuoteOut`), failing closed, and only when it priced
 * exactly `unitCount` units: a quote for another set of units states figures for a charge this
 * dialog is not making.
 */
export function toMatchQuote(body: unknown, unitCount: number): MatchQuote | null {
  const totals = toChargeQuote(body);
  if (totals === null) return null;
  const rawUnits = (body as Record<string, unknown>).units;
  if (!Array.isArray(rawUnits) || rawUnits.length !== unitCount) return null;
  const units: ChargeQuote[] = [];
  for (const raw of rawUnits) {
    const unit = toChargeQuote(raw);
    if (unit === null) return null;
    units.push(unit);
  }
  return { ...totals, units };
}

/** The breakdown of the request a dialog will send: the covered one when the fees are covered. */
export function breakdownFor(quote: ChargeQuote, covered: boolean): ChargeQuoteBreakdown {
  return covered ? quote.covered : quote.uncovered;
}

/** The stated figures a charge carries, so the backend refuses any other (`CHARGE_QUOTE_CHANGED`). */
export interface StatedCharge {
  expected_amount_charged: string;
  expected_fee: string;
}

/** The body fields that hold a charge to a breakdown the admin was shown. */
export function statedCharge(breakdown: ChargeQuoteBreakdown): StatedCharge {
  return { expected_amount_charged: breakdown.amountCharged, expected_fee: breakdown.fee };
}

/** The error code the backend answers when a charge would take other figures than it was sent. */
export const CHARGE_QUOTE_CHANGED = 'CHARGE_QUOTE_CHANGED';
